import { DateTime, Duration } from 'luxon';
import {
  payTimeToDuration,
  timeStringToDuration,
} from '@shared/utilities/utilites';
import { LegResponse } from '../response/leg.response';
import { ReleaseRequest } from '@flight-details/release/models/request/release.request';
import { CrewRequest } from '@flight-details/crew/models/request/crew.request';
import { StandbyListRequest } from '@flight-details/standby-list/models/standby-list.request';

/**
 * Leg View object. Constructor takes LegResponse object and converts it to LegView object. Time strings are
 * stripped of the seconds and nulls are given default values.
 */
export class LegView {
  timestamp: DateTime;
  index: number;
  dutyPeriodIndex: number;
  flightNumber: string;
  depStation: string;
  depTimeSched: DateTime | null;
  depTimeSchedUTC: DateTime | null;
  depTimeResched: DateTime | null;
  depTimeReschedUTC: DateTime | null;
  depTimeActual: DateTime | null;
  depTimeActualUTC: DateTime | null;
  depUtcOffset: number;
  arrStation: string;
  arrTimeSched: DateTime | null;
  arrTimeSchedUTC: DateTime | null;
  arrTimeResched: DateTime | null;
  arrTimeReschedUTC: DateTime | null;
  arrTimeActual: DateTime | null;
  arrTimeActualUTC: DateTime | null;
  arrUtcOffset: number;
  actFlightTimeVsSched: Duration;
  flightTimeSched: Duration;
  flightTimeActual: Duration;
  timeChange: number;
  groundTimeSched: Duration;
  groundTimeActual: Duration;
  odlActual: Duration;
  meal: string;
  aircraft: {
    short: string;
    long: string;
  };
  isDeadhead: boolean;
  atcDelay: Duration;
  rampDelay: Duration;
  deiceDelay: Duration;
  addCode: string;
  removeCode: string;
  isCancelled: boolean;
  airInterrupt: boolean;
  groundInterrupt: boolean;
  isDiversion: boolean;
  diversionTime: Duration;
  isDiversionContinuation: boolean;
  nextLegFailedCont: boolean;
  isSeqEnd: boolean;
  isDepEstimated: boolean;
  isArrEstimated: boolean;
  isDpStart: boolean;
  isDpEnd: boolean;
  isNextLeg = false;

  // eslint-disable-next-line complexity
  constructor(legApi: LegResponse, dutyPeriodIndex: number, timestamp: string) {
    this.timestamp = DateTime.fromISO(timestamp, { zone: 'utc' });
    this.index = legApi.index || 0;
    this.dutyPeriodIndex = dutyPeriodIndex;
    this.flightNumber = legApi.flightNumber || '';
    this.depStation = legApi.depStation || '';
    this.depTimeSched = legApi.depTimeSched
      ? DateTime.fromISO(legApi.depTimeSched, { zone: 'utc' })
      : null;
    this.depTimeSchedUTC = legApi.depTimeSchedUTC
      ? DateTime.fromISO(legApi.depTimeSchedUTC, { zone: 'utc' })
      : null;
    this.depTimeResched = this.getDepTimeResched(
      legApi.depTimeSched,
      legApi.depTimeResched
    );
    this.depTimeReschedUTC = this.getDepTimeResched(
      legApi.depTimeSchedUTC,
      legApi.depTimeReschedUTC
    );
    this.depTimeActual = legApi.depTimeActual
      ? DateTime.fromISO(legApi.depTimeActual, { zone: 'utc' })
      : null;
    this.depTimeActualUTC = legApi.depTimeActualUTC
      ? DateTime.fromISO(legApi.depTimeActualUTC, { zone: 'utc' })
      : null;
    this.arrStation = legApi.arrStation || '';
    this.arrTimeSched = legApi.arrTimeSched
      ? DateTime.fromISO(legApi.arrTimeSched, { zone: 'utc' })
      : null;
    this.arrTimeSchedUTC = legApi.arrTimeSchedUTC
      ? DateTime.fromISO(legApi.arrTimeSchedUTC, { zone: 'utc' })
      : null;
    this.arrTimeResched =
      this.depTimeResched && legApi.arrTimeResched
        ? DateTime.fromISO(legApi.arrTimeResched, { zone: 'utc' })
        : null;
    this.arrTimeReschedUTC =
      this.depTimeReschedUTC && legApi.arrTimeReschedUTC
        ? DateTime.fromISO(legApi.arrTimeReschedUTC, { zone: 'utc' })
        : null;
    this.arrTimeActual = legApi.arrTimeActual
      ? DateTime.fromISO(legApi.arrTimeActual, { zone: 'utc' })
      : null;
    this.arrTimeActualUTC = legApi.arrTimeActualUTC
      ? DateTime.fromISO(legApi.arrTimeActualUTC, { zone: 'utc' })
      : null;
    this.actFlightTimeVsSched = timeStringToDuration(
      legApi.actFlightTimeVsSched || ''
    );
    this.flightTimeSched = timeStringToDuration(legApi.flightTimeSched || '');
    this.flightTimeActual = timeStringToDuration(legApi.flightTimeActual || '');
    this.groundTimeSched = timeStringToDuration(legApi.groundTimeSched || '');
    this.groundTimeActual = timeStringToDuration(legApi.groundTimeActual || '');
    this.odlActual = timeStringToDuration(legApi.odlActual || '');
    this.meal = legApi.meal || '';
    this.aircraft = {
      short: legApi.aircraft?.short || 'Unknown',
      long: legApi.aircraft?.long || 'Unknown',
    };
    this.isDeadhead = legApi.isDeadhead || false;
    this.atcDelay = Duration.fromObject({
      minutes: parseInt(legApi.atcDelay || '0'),
    });
    this.rampDelay = Duration.fromObject({
      minutes: parseInt(legApi.rampDelay || '0'),
    });
    this.deiceDelay = Duration.fromObject({
      minutes: parseInt(legApi.deiceDelay || '0'),
    });
    this.addCode = legApi.addCode || '';
    this.removeCode = legApi.removeCode || '';
    this.isCancelled = legApi.isCancelled || false;
    this.airInterrupt = legApi.airInterrupt || false;
    this.groundInterrupt = legApi.groundInterrupt || false;
    this.isDiversion = legApi.isDiversion || false;
    this.diversionTime = payTimeToDuration(legApi.diversionTime || '');
    this.isDiversionContinuation = legApi.isDiversionContinuation || false;
    this.nextLegFailedCont = legApi.nextLegFailedCont || false;
    this.isSeqEnd = legApi.isSeqEnd || false;
    this.isDepEstimated = legApi.isDepEstimated || false;
    this.isArrEstimated = legApi.isArrEstimated || false;
    this.isDpStart = legApi.isDpStart || false;
    this.isDpEnd = legApi.isDpEnd || false;

    // Calculate UTC offsets and timezone change.
    if (this.depTimeSched && this.depTimeSchedUTC) {
      this.depUtcOffset = this.depTimeSched
        ?.diff(this.depTimeSchedUTC)
        .as('hours');
    } else {
      this.depUtcOffset = 0;
    }

    if (this.arrTimeSched && this.arrTimeSchedUTC) {
      this.arrUtcOffset = this.arrTimeSched
        ?.diff(this.arrTimeSchedUTC)
        .as('hours');
    } else {
      this.arrUtcOffset = 0;
    }

    if (this.arrTimeSched && this.depTimeSched) {
      // Get difference between local dep and arr times.
      const schedDelta = this.arrTimeSched?.diff(this.depTimeSched);

      // Subtract the actual block time to get time diff.
      this.timeChange = Math.round(
        schedDelta.minus(this.flightTimeSched).as('hours')
      );
    } else {
      this.timeChange = 0;
    }

    // console.log('depUtcOffset', this.depUtcOffset);
    // console.log('arrUtcOffset', this.arrUtcOffset);
    // console.log('timeChange', this.timeChange);

    // Modify actual time to include delay times like the HSS.
    this.flightTimeActual = this.flightTimeActual.plus(
      this.atcDelay.plus(this.deiceDelay).plus(this.rampDelay)
    );
  }

  /** Create ReleaseRequest from leg data. */
  getReleaseRequest(): ReleaseRequest | null {
    const request = this.createRequest();

    if (request) {
      return {
        ...request,
        leg: this,
      };
    }

    return null;
  }

  /** Create CrewRequest from leg data. */
  getCrewRequest(): CrewRequest | null {
    const request = this.createRequest();

    if (request) {
      return request as CrewRequest;
    }

    return null;
  }

  /** Create CrewRequest from leg data. */
  getStandbyListRequest(): StandbyListRequest | null {
    const request = this.createRequest();

    if (request) {
      return request as StandbyListRequest;
    }

    return null;
  }

  /** Convenience for extracting common request information. */
  private createRequest(): CrewRequest | null {
    if (this.depTimeSched && this.depStation && this.flightNumber) {
      return {
        date: this.depTimeSched.toISODate(),
        depStation: this.depStation,
        flightNumber: this.flightNumber,
      };
    }

    return null;
  }

  /** Only rescheduled departure times that differ from scheduled should be used. Often a reschedule
   *  only involves an aircraft change.
   */
  private getDepTimeResched(
    depTimeSched: string | undefined,
    depTimeResched: string | undefined
  ): DateTime | null {
    if (depTimeResched && depTimeResched !== depTimeSched) {
      return DateTime.fromISO(depTimeResched, { zone: 'utc' });
    } else {
      return null;
    }
  }
}
